import { GraphqlSubscriptionResult } from '@aws-amplify/api-graphql';
import { useQueryClient } from '@tanstack/react-query';
import React from 'react';
import { fetchGraphqlWithAmplify } from '../../../../../fetcher';

const SubscribeComplianceChecklistDocument = `
subscription subscribeSessionChecklist($id: ID!) {
  subscribeSessionChecklist(id: $id) {
    id
    complianceChecklist {
      items {
        topicTextLower
        status
        date
      }
    }
  }
}
`;

export default function useComplianceSubscription(sessionId?: string) {
  const queryClient = useQueryClient();

  React.useEffect(() => {
    if (!sessionId) return;

    const observable = fetchGraphqlWithAmplify(
      SubscribeComplianceChecklistDocument,
      { id: sessionId },
    ) as unknown as GraphqlSubscriptionResult<any>;

    if (!observable || typeof observable.subscribe !== 'function') {
      console.warn('Subscription observable not available');
      return;
    }
    const subscription = observable.subscribe({
      next(value) {
        const updated = value.data?.subscribeSessionChecklist;
        if (!updated) return;

        queryClient.setQueryData(
          ['getComplianceChecklist', { input: { id: sessionId } }],
          (old: any) => {
            if (!old?.getSession?.complianceChecklist?.items) return old;

            const oldItems = old.getSession.complianceChecklist.items;
            const updatedItems = updated.complianceChecklist.items;

            const oldTopics = new Set(oldItems.map((i) => i.topicTextLower));

            const hasNewTopic = updatedItems.some((i) => !oldTopics.has(i.topicTextLower));

            if (hasNewTopic) {
              queryClient.invalidateQueries({ queryKey: ['getComplianceChecklist', { input: { id: sessionId } }] });
              return old;
            }

            const mergedItems = oldItems.map((oldItem) => {
              const updatedItem = updatedItems.find((u) => u.topicTextLower === oldItem.topicTextLower);
              if (!updatedItem) return oldItem;
              return {
                ...oldItem,
                status: updatedItem.status,
                date: updatedItem.date,
              };
            });

            return {
              ...old,
              getSession: {
                ...old.getSession,
                complianceChecklist: {
                  ...old.getSession.complianceChecklist,
                  items: mergedItems,
                },
              },
            };
          },
        );
      },
      error(error) {
        console.error('Subscription compliance error', error);
      },
    });

    // eslint-disable-next-line consistent-return
    return () => subscription.unsubscribe();
  }, [sessionId, queryClient]);
}
